import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { fileUrl } from '../api';
import './reports.css';

interface Report {
  id: number;
  filename: string;
  user_name: string;
  user_id: number;
  generated_at: string;
  expense_count: number;
  total_amount: number;
  user_currency: string;
  expense_ids: string;
  pdf_filename?: string | null;
}

interface Expense {
  id: number;
  date: string;
  type: string;
  justification: string;
  amount: number;
  currency: string;
  reimbursement_amount: number;
  reimbursement_currency: string;
  exchange_rate: number;
  status: string;
  receipt: string | null;
}

interface UserProfile {
  id?: number;
  email?: string;
  reimbursement_currency?: string;
}

const MyReports = () => {
  const [reports, setReports] = useState([] as any[]);
  const [loading, setLoading] = useState(true);
  const [viewingReport, setViewingReport] = useState<any>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportExpenses, setReportExpenses] = useState<any[]>([]);
  const [excelPreviewHeaders, setExcelPreviewHeaders] = useState<string[]>([]);
  const [excelPreviewRows, setExcelPreviewRows] = useState<any[]>([]);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState<string | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [userProfile, setUserProfile] = useState(null as UserProfile | null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    // Get current user email from localStorage
    const storedEmail = localStorage.getItem('userEmail') || 'unknown';
    setCurrentUserEmail(storedEmail);
    console.log('🔍 Current user email:', storedEmail);
    
    fetchUserProfile();
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/my-reports', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = response.data;
      const myReports = Array.isArray(data) ? data : (Array.isArray(data?.reports) ? data.reports : []);
      setReports(myReports as any[]);
    } catch (error: any) {
      console.error('Failed to fetch reports:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Show helpful error message
      if (error.response?.status === 403) {
        console.error('Authentication failed. Please try logging out and logging back in.');
      } else if (error.response?.status === 401) {
        console.error('No authentication token. Please log in again.');
      }
      
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserProfile(response.data || null);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      setUserProfile(null);
    }
  };

  // Helpers to match Admin Reports formatting
  const formatCurrency = (amount: number | null | undefined, currency: string = '') => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return 'N/A';
    const formattedAmount = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(amount));
    return currency ? `${formattedAmount} ${currency}` : `${formattedAmount}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const parseExpenseIds = (raw: string | null | undefined): string[] => {
    if (!raw) return [];
    // Try JSON first (expected format). Fallback to CSV.
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((x) => String(x).trim()).filter(Boolean);
      }
    } catch {}
    // CSV fallback
    return raw.split(',').map((id) => id.trim()).filter(Boolean);
  };

  const viewReport = async (report: Report) => {
    try {
      // Get the specific expenses for this report
      if (report.expense_ids) {
        const expenseIds = parseExpenseIds(report.expense_ids);
        
        // Fetch all user's expenses
        const response = await api.get('/api/expenses', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const allUserExpenses = response.data || [];
        
        // Filter to only paid expenses that match the report IDs (support multiple id fields like Admin)
        const reportExpenses = allUserExpenses.filter((expense: any) => {
          const isPaid = expense.status === 'paid';
          const candidateIds = [expense.expense_id, expense.unique_id, expense.id]
            .map((v: any) => (v !== undefined && v !== null) ? String(v) : '');
          const matches = candidateIds.some((cid: string) => cid && expenseIds.includes(cid));
          return isPaid && matches;
        });
        
        setReportExpenses(reportExpenses);
      } else {
        setReportExpenses([]);
      }
      
      setViewingReport(report);
      setShowReportModal(true);
      // Reset excel preview state when opening
      setExcelPreviewHeaders([]);
      setExcelPreviewRows([]);
      setExcelError(null);
    } catch (error) {
      console.error('Failed to fetch report expenses:', error);
      setReportExpenses([]);
      setViewingReport(report);
      setShowReportModal(true);
    }
  };

  const loadExcelPreview = async (report: Report) => {
    setExcelLoading(true);
    setExcelError(null);
    try {
      const resp = await api.get(
        `/api/admin/reports/${encodeURIComponent(report.filename)}/json`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const headers: string[] = resp.data?.headers || [];
      const rows: any[][] = resp.data?.rows || [];
      setExcelPreviewHeaders(headers);
      setExcelPreviewRows(rows);
    } catch (err: any) {
      console.error('Failed to load report preview (JSON):', err);
      setExcelError('Failed to load report preview. You can still download the file.');
    } finally {
      setExcelLoading(false);
    }
  };

  const downloadZip = async (report: Report) => {
    try {
      const response = await api.get(`/api/admin/reports/${report.filename}/receipts-zip`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.user_name.replace(/\s+/g, '_')}_receipts_${new Date(report.generated_at).toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download ZIP:', error);
      alert('Failed to download receipts ZIP');
    }
  };

  const downloadPdf = async (report: Report) => {
    try {
      const response = await api.get(`/api/admin/reports/${encodeURIComponent(report.filename)}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', report.pdf_filename || report.filename.replace(/\.xlsx$/, '.pdf'));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to download PDF report');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading your reports...</div>
      </div>
    );
  }

  return (
    <div className="reports-page" style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: 'system-ui, -apple-system, sans-serif', width: '100%', maxWidth: '100vw', overflowX: 'hidden', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ padding: '16px 16px 0 16px', marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
          My Reports
        </h1>
        <p style={{ margin: 0, fontSize: '16px', color: '#6b7280' }}>
          View and download your expense reports
        </p>
      </div>

      <div style={{ padding: '16px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Reports</h3>
            {/* No user filter for My Reports (single user view) */}
          </div>
          {/* Desktop Table View */}
          <div className="reports-table-wrapper" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: '600px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Amount</th>
                  <th style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}># Expenses</th>
                  <th style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No reports found</td>
                  </tr>
                ) : (
                  reports.map((report: any) => (
                    <tr key={report.id}>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>{formatDate(report.generated_at)}</td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{formatCurrency(
                        Number(report.total_amount),
                        userProfile?.reimbursement_currency || report.user_currency || report.currency || ''
                      )}</td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          minWidth: '28px',
                          padding: '2px 8px',
                          borderRadius: '9999px',
                          backgroundColor: '#eef2ff',
                          color: '#4f46e5',
                          fontSize: '12px',
                          fontWeight: 600
                        }}>{report.expense_count}</span>
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                          <button
                            onClick={async () => {
                              try {
                                const response = await api.get(`/api/admin/reports/${encodeURIComponent(report.filename)}/download`, {
                                  headers: { 'Authorization': `Bearer ${token}` },
                                  responseType: 'blob'
                                });
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', report.filename);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                              } catch (error) {
                                console.error('Failed to download report:', error);
                                alert('Failed to download report');
                              }
                            }}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            ⬇️ XLS
                          </button>
                          {report.pdf_filename && (
                            <button
                              onClick={() => downloadPdf(report)}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              📄 PDF
                            </button>
                          )}
                          <button
                            onClick={() => downloadZip(report)}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            📦 ZIP
                          </button>
                          <button
                            onClick={() => viewReport(report)}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#6b7280',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            👁️ View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="reports-cards">
            {reports.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No reports found</div>
            ) : (
              reports.map((report: any) => (
                <div key={report.id} className="reports-card">
                  <div className="reports-card__header">
                    <div className="reports-card__info">
                      <div className="reports-card__date">{formatDate(report.generated_at)}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="reports-card__count">{report.expense_count} expenses</span>
                      </div>
                    </div>
                    <div className="reports-card__amount">
                      {formatCurrency(
                        Number(report.total_amount),
                        userProfile?.reimbursement_currency || report.user_currency || report.currency || ''
                      )}
                    </div>
                  </div>
                  <div className="reports-card__actions">
                    <button
                      onClick={async () => {
                        try {
                          const response = await api.get(`/api/admin/reports/${encodeURIComponent(report.filename)}/download`, {
                            headers: { 'Authorization': `Bearer ${token}` },
                            responseType: 'blob'
                          });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', report.filename);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        } catch (error) {
                          console.error('Failed to download report:', error);
                          alert('Failed to download report');
                        }
                      }}
                      className="reports-card__button reports-card__button--primary"
                    >
                      ⬇️ XLS
                    </button>
                    {report.pdf_filename && (
                      <button
                        onClick={() => downloadPdf(report)}
                        className="reports-card__button reports-card__button--pdf"
                      >
                        📄 PDF
                      </button>
                    )}
                    <button
                      onClick={() => downloadZip(report)}
                      className="reports-card__button reports-card__button--success"
                    >
                      📦 ZIP
                    </button>
                    <button
                      onClick={() => viewReport(report)}
                      className="reports-card__button reports-card__button--view"
                    >
                      👁️ View
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
      </div>

      {/* In-Browser Report Viewing Modal - Same as Admin Panel */}
      {showReportModal && viewingReport && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                📑 Payment Report - {new Date(viewingReport.generated_at).toLocaleDateString()}
              </h2>
              <button
                onClick={() => setShowReportModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Expenses</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>{viewingReport.expense_count}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Amount</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                    {formatCurrency(
                      Number(viewingReport.total_amount),
                      // Prefer the actual reimbursement currency from any expense in this report,
                      // then fallback to current user's reimbursement currency, then report/user currency fields
                      (reportExpenses[0]?.reimbursement_currency) || userProfile?.reimbursement_currency || viewingReport.user_currency || viewingReport.currency || ''
                    )}
                  </div>
                </div>
              </div>
            </div>

            {reportExpenses.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Date</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Description</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Category</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Original Amount</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Reimbursement</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>ID</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportExpenses.map((expense, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                          {new Date(expense.date).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '8px 12px', color: '#1f2937' }}>
                          {expense.justification}
                        </td>
                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                          {expense.type}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#1f2937' }}>
                          {expense.amount?.toFixed(2)} {expense.currency}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600', color: '#1f2937' }}>
                          {expense.reimbursement_amount?.toFixed(2)} {expense.reimbursement_currency}
                        </td>
                        <td style={{ padding: '8px 12px', color: '#1f2937', fontFamily: 'monospace' }}>
                          {expense.expense_id || '—'}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          {expense.receipt ? (
                            <a
                              href={fileUrl(`/uploads/${encodeURIComponent(expense.receipt)}`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#2563eb', textDecoration: 'none' }}
                            >
                              Download
                            </a>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>No receipt</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Excel preview section */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <button
                  onClick={() => loadExcelPreview(viewingReport as any)}
                  disabled={excelLoading}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: excelLoading ? '#d1d5db' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: excelLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {excelLoading ? 'Loading Excel…' : 'Preview Excel (inline)'}
                </button>
                {excelError && <span style={{ color: '#ef4444', fontSize: '12px' }}>{excelError}</span>}
              </div>

              {excelPreviewRows.length > 0 && (
                <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        {excelPreviewHeaders.map((h, idx) => (
                          <th key={idx} style={{ padding: '8px 10px', textAlign: 'left', color: '#374151' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {excelPreviewRows.map((row, rIdx) => (
                        <tr key={rIdx} style={{ borderTop: '1px solid #e5e7eb' }}>
                          {row.map((cell: any, cIdx: number) => (
                            <td key={cIdx} style={{ padding: '8px 10px', color: '#374151' }}>{String(cell ?? '')}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={async () => {
                  try {
                    const response = await api.get(`/api/admin/reports/${encodeURIComponent(viewingReport.filename)}/download`, {
                      headers: { 'Authorization': `Bearer ${token}` },
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', viewingReport.filename);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (error) {
                    console.error('Failed to download report:', error);
                    alert('Failed to download report');
                  }
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Download Excel Report
              </button>
              {viewingReport.pdf_filename && (
                <button
                  onClick={() => downloadPdf(viewingReport)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  📄 Download PDF Report
                </button>
              )}
              <button
                onClick={() => downloadZip(viewingReport)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                📦 Download ZIP Receipts
              </button>
              <button
                onClick={() => loadExcelPreview(viewingReport as any)}
                disabled={excelLoading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: excelLoading ? '#d1d5db' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: excelLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {excelLoading ? 'Loading Excel…' : 'Preview Excel'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MyReports;

